// src/contexts/SocialContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const SocialContext = createContext(null);

export const useSocial = () => {
  const ctx = useContext(SocialContext);
  if (!ctx) throw new Error('useSocial doit être utilisé dans <SocialProvider>');
  return ctx;
};

export const SocialProvider = ({ children }) => {
  const [feed, setFeed] = useState([]);
  const [following, setFollowing] = useState([]);
  const [trending, setTrending] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('token');
  const authHeader = { headers: { 'x-auth-token': token } };

  // ---------- FEED ----------
  const fetchFeed = useCallback(async (opts = {}) => {
    if (!token) return;
    setLoading(true);
    try {
      const url = opts.followingOnly ? '/api/social/feed/following' : '/api/social/feed';
      const params = {};
      if (opts.before) params.before = opts.before;
      if (opts.limit) params.limit = opts.limit;

      const res = await axios.get(url, { ...authHeader, params });

      if (opts.append) {
        setFeed((prev) => [...prev, ...res.data.posts]);
      } else {
        setFeed(res.data.posts);
      }
      return res.data;
    } catch (err) {
      console.error('fetchFeed →', err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // ---------- FOLLOW ----------
  const fetchFollowing = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get('/api/social/following', authHeader);
      setFollowing(res.data.following || []);
    } catch (err) {
      console.error('fetchFollowing →', err.message);
    }
  }, [token]);

  const follow = useCallback(async (userId) => {
    await axios.post(`/api/social/follow/${userId}`, {}, authHeader);
    setFollowing((prev) => [...prev, { _id: userId }]);
  }, [token]);

  const unfollow = useCallback(async (userId) => {
    await axios.delete(`/api/social/follow/${userId}`, authHeader);
    setFollowing((prev) => prev.filter((f) => (f._id || f) !== userId));
  }, [token]);

  const isFollowing = useCallback((userId) => {
    return following.some((f) => (f._id || f) === userId);
  }, [following]);

  // ---------- RÉACTIONS ----------
  const react = useCallback(async (postId, type) => {
    const res = await axios.post(`/api/social/posts/${postId}/react`, { type }, authHeader);
    setFeed((prev) => prev.map((p) => (p._id === postId ? { ...p, ...res.data.post } : p)));
    return res.data.post;
  }, [token]);

  const unreact = useCallback(async (postId) => {
    const res = await axios.delete(`/api/social/posts/${postId}/react`, authHeader);
    setFeed((prev) => prev.map((p) => (p._id === postId ? { ...p, ...res.data.post } : p)));
    return res.data.post;
  }, [token]);

  // ---------- CRÉER / SUPPRIMER ----------
  const createPost = useCallback(async (formData) => {
    const res = await axios.post('/api/social/posts', formData, {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'multipart/form-data',
      },
    });
    setFeed((prev) => [res.data.post, ...prev]);
    return res.data.post;
  }, [token]);

  const deletePost = useCallback(async (postId) => {
    await axios.delete(`/api/social/posts/${postId}`, authHeader);
    setFeed((prev) => prev.filter((p) => p._id !== postId));
  }, [token]);

  const pinPost = useCallback(async (postId, slot) => {
    const res = await axios.put(`/api/social/posts/${postId}/pin`, { slot }, authHeader);
    return res.data.post;
  }, [token]);

  // ---------- SUGGESTIONS ----------
  const fetchSuggestions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get('/api/social/suggestions/follow', authHeader);
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error('fetchSuggestions →', err.message);
    }
  }, [token]);

  // ---------- TRENDING ----------
  const fetchTrending = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get('/api/social/trending', authHeader);
      setTrending(res.data.topPosts || []);
    } catch (err) {
      console.error('fetchTrending →', err.message);
    }
  }, [token]);

  // ---------- REPORT ----------
  const report = useCallback(async (payload) => {
    const res = await axios.post('/api/social/report', payload, authHeader);
    return res.data.report;
  }, [token]);

  // ---------- SHARE DM ----------
  const shareInDM = useCallback(async (postId, recipientId) => {
    const res = await axios.post(`/api/social/posts/${postId}/share-dm`, { recipientId }, authHeader);
    return res.data;
  }, [token]);

  // ---------- VOTE SONDAGE ----------
  const votePoll = useCallback(async (postId, optionIndex) => {
    const res = await axios.post(`/api/social/poll/${postId}/vote`, { optionIndex }, authHeader);
    setFeed((prev) => prev.map((p) => (p._id === postId ? { ...p, poll: res.data.poll } : p)));
    return res.data.poll;
  }, [token]);

  // ---------- CHARGEMENT INITIAL ----------
  useEffect(() => {
    if (token) {
      fetchFeed();
      fetchFollowing();
      fetchSuggestions();
      fetchTrending();
    }
  }, [token, fetchFeed, fetchFollowing, fetchSuggestions, fetchTrending]);

  const value = {
    feed, following, trending, suggestions, loading,
    fetchFeed, fetchFollowing, follow, unfollow, isFollowing,
    react, unreact, createPost, deletePost, pinPost,
    fetchSuggestions, fetchTrending, report, shareInDM, votePoll,
  };

  return <SocialContext.Provider value={value}>{children}</SocialContext.Provider>;
};